import { useNavigate } from 'react-router-dom';
import SeoHead from '../../components/SeoHead';

const sections = [
  {
    id: 1,
    icon: 'ri-translate-2',
    color: 'indigo',
    title: 'Multilingüismo y diversidad cultural',
    content: [
      {
        subtitle: 'Un país, tres comunidades lingüísticas',
        text: 'Bélgica tiene tres comunidades oficiales: la flamenca (neerlandés, 60% de la población), la valona (francés, 40%) y la germanófona (alemán, 1%). Bruselas es oficialmente bilingüe (francés-neerlandés) pero en la práctica es mayoritariamente francófona e internacionalmente anglófona. Conocer el idioma de la región donde trabajarás es fundamental.',
      },
      {
        subtitle: 'El inglés en el entorno internacional',
        text: 'Bruselas es la capital de facto de la Unión Europea y sede de la OTAN, lo que la convierte en una de las ciudades más internacionales del mundo. En instituciones europeas, organizaciones internacionales y empresas multinacionales, el inglés es el idioma de trabajo habitual. Sin embargo, en empresas locales flamencas o valonas, el idioma regional es imprescindible.',
      },
      {
        subtitle: 'La sensibilidad lingüística belga',
        text: 'Las tensiones entre comunidades lingüísticas son una realidad política y social en Bélgica. En el entorno laboral, evita hacer comentarios sobre las diferencias entre flamencos y valones, y nunca asumas que un belga habla el otro idioma nacional. Respetar la identidad lingüística de cada persona es una norma de cortesía fundamental.',
      },
    ],
  },
  {
    id: 2,
    icon: 'ri-government-line',
    color: 'emerald',
    title: 'Instituciones europeas y sector internacional',
    content: [
      {
        subtitle: 'Bruselas: capital de Europa',
        text: 'Bruselas alberga las principales instituciones de la UE (Comisión Europea, Consejo de la UE, Parlamento Europeo), la OTAN, y más de 1.500 organizaciones internacionales y ONGs. Esto crea un mercado laboral único para perfiles con experiencia en política pública, relaciones internacionales, derecho europeo, comunicación y lobbying.',
      },
      {
        subtitle: 'El sector del lobbying y los asuntos europeos',
        text: 'Bruselas tiene la mayor concentración de lobistas del mundo después de Washington D.C. Las empresas multinacionales, asociaciones sectoriales y gobiernos nacionales tienen oficinas de representación en Bruselas. Los perfiles especializados en asuntos europeos, regulación y política pública tienen una demanda muy alta y salarios competitivos.',
      },
      {
        subtitle: 'Sectores clave en Bélgica',
        text: 'Más allá de las instituciones europeas, Bélgica destaca en: industria farmacéutica y biotecnología (Gante, Lieja), tecnología e innovación (Lovaina, Gante), logística y transporte (Amberes, el segundo puerto más grande de Europa), sector financiero (Bruselas), industria química y petroquímica, y el sector del chocolate y la alimentación de alta gama.',
      },
    ],
  },
  {
    id: 3,
    icon: 'ri-user-settings-line',
    color: 'amber',
    title: 'Cultura corporativa y estilo de trabajo',
    content: [
      {
        subtitle: 'Diferencias entre Flandes y Valonia',
        text: 'La cultura laboral varía significativamente entre regiones. Flandes (Amberes, Gante, Brujas) tiene una cultura más cercana a los Países Bajos: directa, eficiente, orientada a resultados. Valonia (Lieja, Namur, Charleroi) y Bruselas tienen una cultura más cercana a Francia: más formal, con mayor énfasis en las relaciones personales y el protocolo.',
      },
      {
        subtitle: 'Consenso y diplomacia',
        text: 'Los belgas son maestros del compromiso y la diplomacia. Dado que el país ha tenido que gestionar durante siglos las tensiones entre comunidades, la cultura del consenso está profundamente arraigada. En el trabajo, esto se traduce en reuniones largas donde se escucha a todos, decisiones consensuadas y una tendencia a evitar los conflictos abiertos.',
      },
      {
        subtitle: 'Formalidad moderada',
        text: 'El nivel de formalidad en Bélgica es intermedio: más formal que los Países Bajos pero menos que Alemania o Austria. En Flandes se usa el tuteo con más facilidad; en Valonia y Bruselas se mantiene el "vous" en contextos profesionales hasta que la relación se consolida. Adapta tu nivel de formalidad a la región y al entorno de la empresa.',
      },
    ],
  },
  {
    id: 4,
    icon: 'ri-scales-3-line',
    color: 'sky',
    title: 'Equilibrio vida-trabajo y derechos laborales',
    content: [
      {
        subtitle: 'Derechos laborales sólidos',
        text: 'Bélgica tiene uno de los sistemas de protección laboral más completos de Europa. El mínimo de vacaciones es de 20 días hábiles, pero la mayoría de convenios ofrecen más. Las bajas por enfermedad están bien reguladas. Los sindicatos tienen una presencia muy fuerte y los convenios colectivos sectoriales son muy importantes para entender las condiciones reales de trabajo.',
      },
      {
        subtitle: 'Beneficios extrasalariales',
        text: 'En Bélgica es muy habitual que el paquete salarial incluya beneficios extrasalariales: coche de empresa (muy común), cheques restaurante (maaltijdcheques/chèques-repas), seguro médico complementario, teléfono móvil, ordenador portátil y planes de pensiones complementarios. Estos beneficios pueden representar el 20-30% del coste total del empleado.',
      },
      {
        subtitle: 'Teletrabajo y flexibilidad',
        text: 'Tras la pandemia, el teletrabajo se ha consolidado en Bélgica. Muchas empresas ofrecen 2-3 días de teletrabajo por semana como estándar. La legislación belga sobre teletrabajo es una de las más avanzadas de Europa. La flexibilidad horaria también es habitual en muchos sectores, especialmente en tecnología y servicios profesionales.',
      },
    ],
  },
  {
    id: 5,
    icon: 'ri-map-pin-line',
    color: 'teal',
    title: 'Vivir en Bélgica: ciudades y calidad de vida',
    content: [
      {
        subtitle: 'Bruselas: cosmopolita e internacional',
        text: 'Bruselas es una de las ciudades más internacionales del mundo: más del 30% de su población es extranjera. Esto facilita enormemente la integración de profesionales de cualquier origen. La ciudad ofrece una calidad de vida muy alta: excelente gastronomía, vida cultural rica, transporte público eficiente y una posición geográfica central en Europa.',
      },
      {
        subtitle: 'Flandes: Amberes, Gante y Brujas',
        text: 'Amberes es el centro financiero y de moda de Flandes, con un puerto de importancia mundial. Gante es una ciudad universitaria vibrante con un sector tecnológico y biotecnológico en auge. Brujas es más turística pero tiene un tejido empresarial sólido. El neerlandés es imprescindible en estas ciudades para integrarse plenamente.',
      },
      {
        subtitle: 'Coste de vida y salarios',
        text: 'Bélgica tiene un coste de vida moderado comparado con Suiza o los Países Bajos. Bruselas es la ciudad más cara. El salario mínimo en 2024 es de aproximadamente 1.994€ brutos/mes (uno de los más altos de Europa). Sin embargo, la presión fiscal es muy alta: los impuestos sobre el trabajo son de los más elevados de la OCDE. Negocia siempre el salario neto o el paquete total.',
      },
    ],
  },
  {
    id: 6,
    icon: 'ri-heart-pulse-line',
    color: 'rose',
    title: 'Sistema social, trámites y networking',
    content: [
      {
        subtitle: 'Registro y trámites administrativos',
        text: 'Al llegar a Bélgica, debes registrarte en el ayuntamiento (commune/gemeente) de tu municipio en los primeros 8 días. Recibirás una tarjeta de identidad de extranjero (eID) que necesitarás para todos los trámites. El número de registro nacional (NISS/INSZ) es tu identificador fiscal y social. Tu empleador normalmente te guiará en este proceso.',
      },
      {
        subtitle: 'Sistema de salud',
        text: 'El sistema de salud belga es de alta calidad y se basa en el reembolso de gastos médicos. Debes afiliarte a una mutualidad (mutualiteit/mutualité) como la Mutualité Chrétienne o la Mutualité Socialiste. La mutualidad reembolsa parte de los gastos médicos. Las cotizaciones sociales se descuentan automáticamente de tu nómina.',
      },
      {
        subtitle: 'Networking en Bélgica',
        text: 'El networking en Bélgica es importante pero discreto. Las asociaciones profesionales, las cámaras de comercio (como la Chambre de Commerce belgo-espagnole) y los eventos de las instituciones europeas son excelentes puntos de entrada. LinkedIn es muy utilizado en el entorno profesional belga. Las relaciones se construyen con paciencia y consistencia.',
      },
    ],
  },
];

const tips = [
  { icon: 'ri-check-double-line', text: 'Regístrate en el ayuntamiento en los primeros 8 días de llegada' },
  { icon: 'ri-check-double-line', text: 'Afíliate a una mutualidad para acceder al sistema de salud' },
  { icon: 'ri-check-double-line', text: 'Aprende el idioma de la región donde vayas a trabajar' },
  { icon: 'ri-check-double-line', text: 'Negocia el paquete salarial completo, no solo el salario bruto' },
  { icon: 'ri-check-double-line', text: 'Respeta la sensibilidad lingüística entre flamencos y valones' },
  { icon: 'ri-check-double-line', text: 'Investiga el convenio colectivo de tu sector antes de firmar' },
];

const colorMap: Record<string, { bg: string; text: string; border: string; light: string }> = {
  indigo: { bg: 'bg-indigo-600', text: 'text-indigo-600', border: 'border-indigo-200', light: 'bg-indigo-50' },
  emerald: { bg: 'bg-emerald-600', text: 'text-emerald-600', border: 'border-emerald-200', light: 'bg-emerald-50' },
  amber: { bg: 'bg-amber-600', text: 'text-amber-600', border: 'border-amber-200', light: 'bg-amber-50' },
  sky: { bg: 'bg-sky-600', text: 'text-sky-600', border: 'border-sky-200', light: 'bg-sky-50' },
  teal: { bg: 'bg-teal-600', text: 'text-teal-600', border: 'border-teal-200', light: 'bg-teal-50' },
  rose: { bg: 'bg-rose-600', text: 'text-rose-600', border: 'border-rose-200', light: 'bg-rose-50' },
};

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Claves culturales para trabajar en Bélgica',
  description: 'Bilingüismo, diversidad regional, instituciones europeas y networking profesional en Bélgica. Guía para integrarse en el mercado laboral belga.',
  url: `${siteUrl}/guia-claves-belgica`,
  author: { '@type': 'Organization', name: 'BN ĀGENZIA' },
  publisher: { '@type': 'Organization', name: 'BN ĀGENZIA', url: siteUrl },
  inLanguage: 'es',
  about: { '@type': 'Thing', name: 'Mercado laboral Bélgica' },
  dateModified: '2025-07-14',
};

export default function GuiaClaveBelgicaPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 print:bg-white">
      <SeoHead
        title="Claves culturales para trabajar en Bélgica – Guía | BN ĀGENZIA"
        description="Bilingüismo, diversidad regional, instituciones europeas y networking en Bélgica. Todo lo que necesitas para integrarte en el mercado laboral belga."
        keywords="trabajar en Bélgica, cultura laboral belga, empleo Bélgica, mercado laboral Bélgica, instituciones europeas Bruselas"
        canonical="/guia-claves-belgica"
        schema={schema}
      />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm print:hidden">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/perfil?tab=recursos')}
              className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#1A2B4A] transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              Volver a recursos
            </button>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full text-xs font-semibold">
                🇧🇪 Bélgica
              </span>
              <button
                onClick={() => window.print()}
                className="flex items-center gap-2 px-4 py-2 bg-[#1A2B4A] text-white text-sm font-medium rounded-lg hover:bg-[#2A3B5A] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-printer-line"></i>
                Imprimir / Guardar PDF
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-10 print:py-4 print:px-8">
        <div className="bg-gradient-to-r from-[#1A2B4A] to-[#2A3B5A] rounded-2xl p-10 mb-10">
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0 text-4xl">
              🇧🇪
            </div>
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="px-3 py-1 bg-indigo-400/20 text-indigo-300 border border-indigo-400/30 rounded-full text-xs font-semibold uppercase tracking-wide">
                  Guía cultural
                </span>
                <span className="text-white/50 text-xs">6 secciones · Lectura: ~12 min</span>
              </div>
              <h1 className="text-3xl font-bold text-white mb-3 leading-tight">
                Claves culturales para trabajar en Bélgica
              </h1>
              <p className="text-white/80 text-base leading-relaxed max-w-2xl">
                Navega el entorno multicultural belga: bilingüismo, diversidad regional, instituciones europeas y networking profesional en la capital de Europa.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-10">
          <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-list-check-2 text-[#1A2B4A]"></i>
            Contenido de la guía
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {sections.map((section) => {
              const c = colorMap[section.color];
              return (
                <a key={section.id} href={`#seccion-${section.id}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <span className={`w-7 h-7 ${c.bg} text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0`}>
                    {section.id}
                  </span>
                  <span className="text-sm text-gray-700 group-hover:text-[#1A2B4A] font-medium transition-colors">
                    {section.title}
                  </span>
                </a>
              );
            })}
          </div>
        </div>

        <div className="space-y-8">
          {sections.map((section) => {
            const c = colorMap[section.color];
            return (
              <div key={section.id} id={`seccion-${section.id}`}
                className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden print:break-inside-avoid"
              >
                <div className={`${c.light} ${c.border} border-b px-6 py-5 flex items-center gap-4`}>
                  <div className={`w-11 h-11 ${c.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <i className={`${section.icon} text-white text-xl`}></i>
                  </div>
                  <div>
                    <span className={`text-xs font-semibold ${c.text} uppercase tracking-wide`}>Sección {section.id}</span>
                    <h2 className="text-lg font-bold text-gray-900 leading-tight">{section.title}</h2>
                  </div>
                </div>
                <div className="p-6 space-y-6">
                  {section.content.map((item, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className={`w-1 rounded-full ${c.bg} flex-shrink-0 mt-1`} style={{ minHeight: '100%' }}></div>
                      <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-1.5">{item.subtitle}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 bg-white rounded-xl border border-gray-200 shadow-sm p-8 print:break-inside-avoid">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 bg-[#1A2B4A] rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-checkbox-multiple-line text-white text-xl"></i>
            </div>
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Antes de llegar</span>
              <h2 className="text-lg font-bold text-gray-900">Checklist esencial</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {tips.map((tip, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                <i className="ri-check-double-line text-emerald-600 text-lg flex-shrink-0 mt-0.5"></i>
                <p className="text-sm text-gray-700 leading-relaxed">{tip.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 bg-gradient-to-r from-[#1A2B4A] to-[#2A3B5A] rounded-xl p-8 text-center print:hidden">
          <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-customer-service-2-line text-white text-2xl"></i>
          </div>
          <h3 className="text-xl font-bold text-white mb-2">¿Tienes dudas sobre el mercado belga?</h3>
          <p className="text-white/80 text-sm mb-6 max-w-lg mx-auto leading-relaxed">
            Nuestro equipo tiene experiencia directa en el mercado laboral belga y puede orientarte de forma personalizada.
          </p>
          <button
            onClick={() => navigate('/#contacto')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#1A2B4A] text-sm font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-send-line"></i>
            Contactar con el equipo
          </button>
        </div>

        <div className="mt-8 text-center text-xs text-gray-400 pb-8 print:mt-4">
          <p>Guía elaborada por el equipo de BN Agenzia · Uso exclusivo para candidatos registrados</p>
        </div>
      </div>

      <style>{`
        @media print {
          @page { margin: 1.5cm; }
          body { font-size: 11pt; }
        }
      `}</style>
    </div>
  );
}
